import type { ReactNode } from 'react';
import { Link, useNavigate } from '@tanstack/react-router';
import { Avatar } from '../components/Avatar';
import { Logo } from '../components/Logo';
import { NotificationsBell } from './NotificationsBell';
import { PostLoginModalBanner } from './PostLoginModalBanner';
import { ThemeToggle } from './ThemeToggle';
import { TopPageBanner } from './TopPageBanner';
import { BrandProvider } from '../brand/BrandProvider';
import { useActiveBrand } from '../api/brand';
import { useStaffContext } from '../api/hooks';
import { logout } from '../api/auth';

/**
 * Minimal shell for business_staff users — no sidebar, no business switcher,
 * just brand + theme toggle + sign-out. Their job is to click "Open POS".
 * Business name + staff name come from GET /staff/context (de-mocked #56; the
 * shared query key means the landing page reuses the same fetch).
 *
 * Receives platform broadcasts (TopPageBanner + PostLoginModalBanner) so
 * cashiers see maintenance notices the same as everyone else.
 */
export function StaffShell({ children }: { children: ReactNode }) {
  const brand = useActiveBrand();
  const navigate = useNavigate();
  const { data } = useStaffContext();
  const businessName = data?.businessName ?? brand.name;
  const staffName = data?.staffName ?? '—';

  async function handleSignOut() {
    await logout();
    void navigate({ to: '/login' });
  }

  return (
    <BrandProvider brand={brand}>
    <div className="min-h-screen bg-zinc-50 dark:bg-zinc-950">
      <TopPageBanner />
      <header className="sticky top-0 z-40 h-14 border-b border-zinc-200 bg-white/80 backdrop-blur-md dark:bg-zinc-950/80 dark:border-zinc-700">
        <div className="h-full px-4 sm:px-5 flex items-center gap-3">
          <Link to="/staff" className="flex items-center gap-2 -ml-1 px-1.5 py-1 rounded-md hover:bg-zinc-100 dark:hover:bg-zinc-800 transition">
            <Logo />
            <span className="font-semibold text-zinc-950 dark:text-zinc-50 tracking-tight">
              {businessName} POS
            </span>
          </Link>

          <div className="ml-auto flex items-center gap-2">
            <ThemeToggle />
            <NotificationsBell />
            <button type="button" onClick={handleSignOut} className="ml-1" aria-label="Sign out">
              <Avatar name={staffName} size="md" />
            </button>
          </div>
        </div>
      </header>

      <main className="min-h-[calc(100vh-3.5rem)]">{children}</main>
      <PostLoginModalBanner />
    </div>
    </BrandProvider>
  );
}
